import { AxiosResponse } from "axios";
import { TFunction } from "i18next";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { SeverityEnum } from "../../shared/enum/SeverityEnum";
import { FileUtils } from "../../shared/utils/FileUtils";
import { DecisionTree } from "../models/DecisionTree";
import { AdminDecisionTreeService } from "../services/AdminDecisionTreeService";

interface Props extends WithTranslation {
}

interface States {
  decisionTrees: DecisionTree[];
  displayConfirmation: boolean;
  toDelete: string | null;
}

class AdminDecisionTrees extends React.Component<Props, States> {
  private readonly decisionTreeService: AdminDecisionTreeService;
  private toast: Toast | null;
  private fileUpload: FileUpload | null;
  private t: TFunction;

  constructor(props: Props) {
    super(props);
    this.decisionTreeService = new AdminDecisionTreeService();
    this.t = this.props.t;
    this.toast = null;
    this.fileUpload = null;
    this.state = {
      decisionTrees: [],
      displayConfirmation: false,
      toDelete: null,
    };
  }

  componentDidMount() {
    this.getDecisionTrees();
  }

  /**
   * Refresh decision trees
   */
  getDecisionTrees() {
    this.decisionTreeService
      .getMany()
      .then((response: AxiosResponse<DecisionTree[]>) => {
        this.setState({ decisionTrees: response.data });
      });
  }

  /**
   * On upload
   */
  handleUpload(e: { files: File[] }) {
    if (!e.files) {
      return;
    }
    this.decisionTreeService
      .uploadMany(e.files)
      .then(() => {
        this.toast?.show({
          severity: SeverityEnum.SUCCESS,
          detail: this.t("UI.ADMIN.DECISION_TREE.UPLOAD.TOAST_SUCCESS"),
        });
        this.fileUpload?.clear();
        this.getDecisionTrees();
      })
      .catch((err: any) => {
        const message = err?.response?.data?.message;
        this.toast?.show({
          severity: SeverityEnum.ERROR,
          detail: `${this.t("UI.ADMIN.DECISION_TREE.UPLOAD.TOAST_ERROR")}: ${message}`,
        });
      });
  }

  /**
   * On download
   */
  handleDownload(name: string) {
    this.decisionTreeService
      .downloadOne(name)
      .then((response: AxiosResponse<Blob>) => {
        FileUtils.downloadFile(response);
      });
  }

  /**
   * Delete decision tree
   */
  handleDelete() {
    if (!this.state.toDelete) {
      return;
    }

    this.decisionTreeService.deleteOne(this.state.toDelete).then(() => {
      this.toast?.show({
        severity: SeverityEnum.SUCCESS,
        detail: this.t("UI.ADMIN.DECISION_TREE.DELETE.TOAST_SUCCESS"),
      });
      this.setState({ displayConfirmation: false, toDelete: null });
      this.getDecisionTrees();
    });
  }

  renderDialogFooter() {
    return (
      <div>
        <Button
          label="No"
          icon="pi pi-times"
          onClick={() => this.setState({ displayConfirmation: false })}
          className="p-button-text"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          onClick={() => this.handleDelete()}
          autoFocus
        />
      </div>
    );
  }

  actionBodyTemplate(rowData: DecisionTree) {
    return (
      <React.Fragment>
        <Button
          type="button"
          icon="pi pi-download"
          className="p-button-secondary p-mr-1"
          onClick={() => this.handleDownload(rowData.name)}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-danger"
          onClick={() =>
            this.setState({ displayConfirmation: true, toDelete: rowData.name })
          }
        />
      </React.Fragment>
    );
  }

  render() {
    return (
      <Card title={this.t("UI.ADMIN.DECISION_TREE.TITLE")}>
        <Toast ref={(el) => (this.toast = el)}/>
        <div className="grid">
          <div className="col-12">
            <FileUpload
              ref={(el) => (this.fileUpload = el)}
              mode="basic"
              accept="application/json"
              customUpload={true}
              multiple={true}
              uploadHandler={(e: { files: File[] }) => this.handleUpload(e)}
              // emptyTemplate={() => <p className="p-m-0">{this.t("ui_admin_decision_tree_upload_content")}</p>}
              chooseLabel={this.t("UI.BUTTON.CHOOSE_FILE")}
              cancelLabel={this.t("UI.BUTTON.CANCEL_FILE")}
              uploadLabel={this.t("UI.BUTTON.UPLOAD_FILE")}
            />
          </div>
          <div className="col-12">
            <DataTable
              value={this.state.decisionTrees}
              rows={5}
              paginator={true}
              rowsPerPageOptions={[5, 10, 20]}
            >
              <Column
                field="name"
                filter
                filterPlaceholder={this.t("UI.ADMIN.DECISION_TREE.SEARCH_BY_NAME")}
                header={this.t("UI.ADMIN.DECISION_TREE.NAME")}
              />
              <Column
                body={(rowData: DecisionTree) =>
                  this.actionBodyTemplate(rowData)
                }
                headerStyle={{ width: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
              />
            </DataTable>
            <Dialog
              header="Confirmation"
              visible={this.state.displayConfirmation}
              modal
              style={{ width: "350px" }}
              footer={this.renderDialogFooter()}
              onHide={() => this.setState({ displayConfirmation: false })}
            >
              <div className="confirmation-content">
                <i
                  className="pi pi-exclamation-triangle p-mr-3"
                  style={{ fontSize: "2rem" }}
                />
                <span>{this.t("UI.CONFIRM_DIALOG.TEXT")}</span>
              </div>
            </Dialog>
          </div>
        </div>
      </Card>
    );
  }
}

export default withTranslation()(AdminDecisionTrees);
